/**
 * Chart colours for the Lushin analytics surfaces.
 *
 * VALIDATED, NOT CHOSEN BY EYE. Every categorical step below was run through the
 * six-check validator (lightness band, chroma floor, CVD separation under
 * protanopia/deuteranopia, a normal-vision floor, and contrast against the chart
 * surface) and passes in both modes.
 *
 * Two things worth knowing before editing:
 *
 * 1. THE ORDER IS PART OF THE PALETTE. The checks run on ADJACENT pairs, so
 *    re-ordering can break separation even though the same colours are present.
 *    Amber next to rose fails at ΔE 5.7 under deuteranopia; separating them with
 *    indigo is what makes this set pass at 13.6.
 *
 * 2. DARK IS ITS OWN SET, NOT A FLIP. The light steps are too dark on the ink
 *    surface and fall outside the dark lightness band (0.48–0.67 vs 0.43–0.77).
 *    Same five hue families, stepped independently per mode.
 *
 * The Lushin brand badge colours (plasma-cyan, and the score-band greens/ambers)
 * are deliberately NOT used as chart marks — they were picked for chips on dark
 * surfaces and fail the lightness band and contrast checks outright on a light
 * chart surface. They remain correct as status badges, which is a different job.
 */

/** Categorical hues, in fixed order. Never cycle past the end — fold into "Other". */
export const CATEGORICAL_LIGHT = [
  '#0D9488', // teal
  '#B45309', // amber
  '#4338CA', // indigo
  '#BE123C', // rose
  '#6D28D9' // violet
] as const;

export const CATEGORICAL_DARK = [
  '#0EA5A0',
  '#D97706',
  '#6366F1',
  '#F43F5E',
  '#8B5CF6'
] as const;

/**
 * The single hue used whenever a chart encodes MAGNITUDE rather than identity —
 * a ranked bar of dimension scores, a tenant×dimension heatmap. Light to dark,
 * one hue, never a rainbow.
 */
export const SEQUENTIAL_TEAL = [
  '#CCFBF1',
  '#99F6E4',
  '#5EEAD4',
  '#2DD4BF',
  '#14B8A6',
  '#0D9488',
  '#0F766E'
] as const;

/**
 * Score bands, matching the rubric's own interpretation scale.
 *
 * These are STATUS colours: reserved, never reused as "series 6", and always
 * shipped with a text label beside them rather than carrying meaning by colour
 * alone. That is also why they are exempt from the categorical checks — a lone
 * status colour is judged on text contrast, not on separation from its
 * neighbours.
 */
export interface ScoreBand {
  key: string;
  label: string;
  /** Inclusive lower bound as a percentage of the 50-point maximum. */
  min: number;
  color: string;
  /** Paired with the colour so status never depends on hue alone. */
  icon: string;
}

export const SCORE_BANDS: ScoreBand[] = [
  { key: 'standard', label: 'The standard', min: 90, color: '#0F766E', icon: '★' },
  { key: 'strong', label: 'Strong', min: 76, color: '#15803D', icon: '▲' },
  { key: 'soft', label: 'Soft', min: 60, color: '#A16207', icon: '●' },
  { key: 'at_risk', label: 'At risk', min: 40, color: '#C2410C', icon: '▼' },
  { key: 'lost', label: 'Lost / reset', min: 0, color: '#BE123C', icon: '■' }
];

/** The band a 0-100 score falls into. */
export function bandFor(scorePercent: number | null | undefined): ScoreBand {
  if (scorePercent == null) return SCORE_BANDS[SCORE_BANDS.length - 1];
  return SCORE_BANDS.find((b) => scorePercent >= b.min) ?? SCORE_BANDS[SCORE_BANDS.length - 1];
}

/**
 * Below this, a dimension is called out as a weak area.
 *
 * 60% is the rubric's own "soft call" boundary (30/50) — the point at which it
 * says the deal is alive on hope rather than on merit. Using the rubric's
 * threshold rather than an invented one means the UI and the coaching language
 * agree about what "weak" means.
 */
export const WEAK_AREA_THRESHOLD = 60;

/** Recessive chart furniture, so the data stays the loudest thing on screen. */
export const CHART_INK = {
  grid: '#E4E1DA',
  axis: '#6B6F7A',
  label: '#0A0B0F',
  muted: '#A7ABB4',
  reference: '#C2410C'
} as const;
